import { useCallback, useRef, useState } from "react";
import classes from "./ProductCarouselSection.module.css";

const MAX_DOTS = 5;

// Shared shell (background/header/horizontal scroll-snap track/dots) for the three mobile
// product sections — only the background color, card width and per-item render differ per
// variant, so the layout itself never gets duplicated three times.
export function ProductCarouselSection({
  variant,
  title,
  subtitle,
  icon,
  viewAllLabel,
  onViewAll,
  items,
  renderItem,
  maxItems,
}) {
  const trackRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);

  const visibleItems = maxItems ? items.slice(0, maxItems) : items;
  const dotCount = Math.min(visibleItems.length, MAX_DOTS);
  const cardWrapClass =
    variant === "bus"
      ? classes.busCardWrap
      : variant === "flightHotel"
        ? classes.flightHotelCardWrap
        : classes.cardWrap;

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el || dotCount <= 1) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const progress = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
    setActiveDot(Math.round(progress * (dotCount - 1)));
  }, [dotCount]);

  const scrollToDot = (index) => {
    const el = trackRef.current;
    if (!el || dotCount <= 1) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: (index / (dotCount - 1)) * maxScroll, behavior: "smooth" });
  };

  if (visibleItems.length === 0) return null;

  return (
    <section className={`${classes.section} ${classes[variant]}`}>
      <div className={classes.header}>
        <div className={classes.headerMain}>
          <span className={classes.icon}>{icon}</span>
          <div className={classes.titleBlock}>
            <h2 className={classes.title}>{title}</h2>
            <p className={classes.subtitle}>{subtitle}</p>
          </div>
        </div>
        {onViewAll && (
          <button type="button" className={classes.viewAll} onClick={onViewAll}>
            {viewAllLabel} →
          </button>
        )}
      </div>

      <div className={classes.track} ref={trackRef} onScroll={onScroll}>
        {visibleItems.map((item) => (
          <div key={item.id} className={cardWrapClass}>
            {renderItem(item)}
          </div>
        ))}
      </div>

      {dotCount > 1 && (
        <div className={classes.dots}>
          {Array.from({ length: dotCount }, (_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`slide ${i + 1}`}
              className={`${classes.dot} ${i === activeDot ? classes.dotActive : ""}`}
              onClick={() => scrollToDot(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
